import React, { useMemo, useState } from 'react';
import { ArrowLeft, Building2, Check, Mail, Phone, Settings2, User, X } from 'lucide-react';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { LW_THEME_CLASS } from './shared/InvestorThemeScope';
import { PRODUCT_TYPES, type Investor, type NetWorthRequirements, type EpoRule, type EpdRule, type EpoRuleType } from '@/types/investor';
import { formatPhoneNumber, formatUrl, getOptionalNumberError, isTenDigitPhone, isValidUrl, parseOptionalNumber } from '@/utils/investor/validation';
import { generateId } from '@/utils/investor/storage';
import { createEpdRule, createEpoRule, getDefaultEpdRule, getDefaultEpoRule, getRuleValueError, getValidEpdTypes, getValidEpoTypes, normalizeRuleValue } from '@/utils/investor/businessRules';

type ChannelView = 'wholesale' | 'correspondent';
type Status = 'Approved' | 'Pending' | 'Inactive';
type StepKey = 'identity' | 'channels' | 'products' | 'contacts' | 'terms';

interface AddInvestorWizardProps {
  open: boolean;
  defaultChannel?: ChannelView;
  existingInvestors: Investor[];
  onOpenChange: (open: boolean) => void;
  onCreate: (investor: Investor) => Promise<void> | void;
}

interface WizardState {
  investor: string;
  website: string;
  canDoWholesale: boolean;
  canGoCorrespondent: boolean;
  status: Status;
  approvedAsOfDate: string;
  correspondentStatus: Status;
  correspondentApprovedAsOfDate: string;
  wholesaleProducts: string[];
  correspondentProducts: string[];
  wholesaleAeName: string;
  wholesaleAeEmail: string;
  wholesaleAePhone: string;
  correspondentAeName: string;
  correspondentAeEmail: string;
  correspondentAePhone: string;
  wholesaleLenderPaidComp: string;
  wholesaleAdditionalLendwiseFee: string;
  wholesaleEpoRule: EpoRule;
  wholesaleEpdRule: EpdRule;
  correspondentEpoRule: EpoRule;
  correspondentEpdRule: EpdRule;
  netWorthDelegated: string;
  netWorthNonDelegated: string;
}

const STEPS: Array<{ key: StepKey; label: string }> = [
  { key: 'identity', label: 'Identity' },
  { key: 'channels', label: 'Channels & Status' },
  { key: 'products', label: 'Products' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'terms', label: 'Terms' }
];

const emailOk = (value: string) => !value.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const statusOptions: Status[] = ['Approved', 'Pending', 'Inactive'];

const buildInitialState = (defaultChannel?: ChannelView): WizardState => ({
  investor: '',
  website: '',
  canDoWholesale: defaultChannel !== 'correspondent',
  canGoCorrespondent: defaultChannel === 'correspondent',
  status: 'Pending',
  approvedAsOfDate: '',
  correspondentStatus: 'Pending',
  correspondentApprovedAsOfDate: '',
  wholesaleProducts: [],
  correspondentProducts: [],
  wholesaleAeName: '',
  wholesaleAeEmail: '',
  wholesaleAePhone: '',
  correspondentAeName: '',
  correspondentAeEmail: '',
  correspondentAePhone: '',
  wholesaleLenderPaidComp: '',
  wholesaleAdditionalLendwiseFee: '',
  wholesaleEpoRule: getDefaultEpoRule('wholesale'),
  wholesaleEpdRule: getDefaultEpdRule('wholesale'),
  correspondentEpoRule: getDefaultEpoRule('correspondent'),
  correspondentEpdRule: getDefaultEpdRule('correspondent'),
  netWorthDelegated: '',
  netWorthNonDelegated: ''
});

const toggleValue = (values: string[], value: string) =>
  values.includes(value) ? values.filter((item) => item !== value) : [...values, value];

export const AddInvestorWizard: React.FC<AddInvestorWizardProps> = ({
  open,
  defaultChannel,
  existingInvestors,
  onOpenChange,
  onCreate
}) => {
  const [step, setStep] = useState(0);
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState<WizardState>(() => buildInitialState(defaultChannel));

  const setField = <K extends keyof WizardState>(key: K, value: WizardState[K]) => {
    setState((prev) => ({ ...prev, [key]: value }));
  };

  const errors = useMemo(() => {
    const next: Record<string, string> = {};
    const name = state.investor.trim();
    const formattedWebsite = formatUrl(state.website);

    if (!name) {
      next.investor = 'Investor name is required';
    } else if (existingInvestors.some((investor) => investor.investor.trim().toLowerCase() === name.toLowerCase())) {
      next.investor = 'An investor with this name already exists';
    }

    if (formattedWebsite && !isValidUrl(formattedWebsite)) {
      next.website = 'Enter a valid website URL';
    }

    if (state.wholesaleAePhone.trim() && !isTenDigitPhone(state.wholesaleAePhone)) {
      next.wholesaleAePhone = 'Enter a 10-digit phone number';
    }
    if (state.correspondentAePhone.trim() && !isTenDigitPhone(state.correspondentAePhone)) {
      next.correspondentAePhone = 'Enter a 10-digit phone number';
    }

    if (!state.canDoWholesale && !state.canGoCorrespondent) {
      next.channels = 'Enable at least one channel';
    }

    if (state.canDoWholesale && state.status === 'Approved' && !state.approvedAsOfDate) {
      next.approvedAsOfDate = 'Effective date is required when Wholesale is Approved';
    }

    if (state.canGoCorrespondent && state.correspondentStatus === 'Approved' && !state.correspondentApprovedAsOfDate) {
      next.correspondentApprovedAsOfDate = 'Effective date is required when Correspondent is Approved';
    }

    if (!emailOk(state.wholesaleAeEmail)) {
      next.wholesaleAeEmail = 'Enter a valid email address';
    }

    if (!emailOk(state.correspondentAeEmail)) {
      next.correspondentAeEmail = 'Enter a valid email address';
    }

    if (state.canDoWholesale) {
      // Matches the DB CHECK investors_wholesale_comp_range (1.00–2.50 or blank).
      const lpcError = getOptionalNumberError(state.wholesaleLenderPaidComp, 'LPC %', { min: 1, max: 2.5 });
      const feeError = getOptionalNumberError(state.wholesaleAdditionalLendwiseFee, 'Additional Lender Fee', { min: 0 });
      if (lpcError) {
        next.wholesaleLenderPaidComp = lpcError;
      }
      if (feeError) {
        next.wholesaleAdditionalLendwiseFee = feeError;
      }
      const wEpoError = getRuleValueError('Wholesale EPO', state.wholesaleEpoRule, 'wholesale', 'epo');
      const wEpdError = getRuleValueError('Wholesale EPD', state.wholesaleEpdRule, 'wholesale', 'epd');
      if (wEpoError) {
        next.wholesaleEpoRule = wEpoError;
      }
      if (wEpdError) {
        next.wholesaleEpdRule = wEpdError;
      }
    }

    if (state.canGoCorrespondent) {
      const delegatedError = getOptionalNumberError(state.netWorthDelegated, 'Delegated net worth', { min: 0 });
      const nonDelegatedError = getOptionalNumberError(state.netWorthNonDelegated, 'Non-delegated net worth', { min: 0 });
      if (delegatedError) {
        next.netWorthDelegated = delegatedError;
      }
      if (nonDelegatedError) {
        next.netWorthNonDelegated = nonDelegatedError;
      }
      const cEpoError = getRuleValueError('Correspondent EPO', state.correspondentEpoRule, 'correspondent', 'epo');
      const cEpdError = getRuleValueError('Correspondent EPD', state.correspondentEpdRule, 'correspondent', 'epd');
      if (cEpoError) {
        next.correspondentEpoRule = cEpoError;
      }
      if (cEpdError) {
        next.correspondentEpdRule = cEpdError;
      }
    }

    return next;
  }, [existingInvestors, state]);

  const stepErrorKeys: string[][] = [
    ['investor', 'website'],
    ['channels', 'approvedAsOfDate', 'correspondentApprovedAsOfDate'],
    [],
    ['wholesaleAeEmail', 'wholesaleAePhone', 'correspondentAeEmail', 'correspondentAePhone'],
    ['wholesaleLenderPaidComp', 'wholesaleAdditionalLendwiseFee', 'netWorthDelegated', 'netWorthNonDelegated', 'wholesaleEpoRule', 'wholesaleEpdRule', 'correspondentEpoRule', 'correspondentEpdRule']
  ];

  const stepHasErrors = (index: number) => stepErrorKeys[index].some((key) => errors[key]);

  const resetAndClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setState(buildInitialState(defaultChannel));
      setStep(0);
      setTouched(false);
      setSubmitting(false);
    }, 200);
  };

  const goNext = () => {
    setTouched(true);
    if (stepHasErrors(step)) {
      return;
    }
    setTouched(false);
    setStep((current) => Math.min(current + 1, STEPS.length - 1));
  };

  const goBack = () => {
    setTouched(false);
    setStep((current) => Math.max(current - 1, 0));
  };

  const submit = async () => {
    setTouched(true);
    if (Object.keys(errors).length > 0) {
      const firstErrorStep = stepErrorKeys.findIndex((keys) => keys.some((key) => errors[key]));
      if (firstErrorStep >= 0) {
        setStep(firstErrorStep);
      }
      return;
    }

    const website = formatUrl(state.website);
    const delegatedNetWorth = parseOptionalNumber(state.netWorthDelegated);
    const nonDelegatedNetWorth = parseOptionalNumber(state.netWorthNonDelegated);
    const netWorthRequirements: NetWorthRequirements = {
      ...(delegatedNetWorth !== undefined ? { delegated: delegatedNetWorth } : {}),
      ...(nonDelegatedNetWorth !== undefined ? { nonDelegated: nonDelegatedNetWorth } : {})
    };
    const now = new Date().toISOString();

    const investor: Investor = {
      id: generateId(),
      investor: state.investor.trim(),
      productType: Array.from(new Set([...state.wholesaleProducts, ...state.correspondentProducts])),
      docType: [],
      canDoWholesale: state.canDoWholesale,
      canGoCorrespondent: state.canGoCorrespondent,
      status: state.canDoWholesale ? state.status : 'Inactive',
      approvedAsOfDate: state.canDoWholesale && state.status === 'Approved' ? state.approvedAsOfDate : undefined,
      correspondentStatus: state.canGoCorrespondent ? state.correspondentStatus : 'Inactive',
      correspondentApprovedAsOfDate: state.canGoCorrespondent && state.correspondentStatus === 'Approved' ? state.correspondentApprovedAsOfDate : undefined,
      wholesaleProducts: state.canDoWholesale ? state.wholesaleProducts : [],
      correspondentProducts: state.canGoCorrespondent ? state.correspondentProducts : [],
      wholesaleLenderPaidComp: parseOptionalNumber(state.wholesaleLenderPaidComp) ?? null,
      wholesaleAdditionalLendwiseFee: parseOptionalNumber(state.wholesaleAdditionalLendwiseFee) ?? null,
      netWorthRequirements,
      wholesaleAeName: state.canDoWholesale ? state.wholesaleAeName.trim() : '',
      wholesaleAePhone: state.canDoWholesale ? formatPhoneNumber(state.wholesaleAePhone) : '',
      wholesaleAeEmail: state.canDoWholesale ? state.wholesaleAeEmail.trim() : '',
      correspondentAeName: state.canGoCorrespondent ? state.correspondentAeName.trim() : '',
      correspondentAePhone: state.canGoCorrespondent ? formatPhoneNumber(state.correspondentAePhone) : '',
      correspondentAeEmail: state.canGoCorrespondent ? state.correspondentAeEmail.trim() : '',
      aeName: state.wholesaleAeName.trim() || state.correspondentAeName.trim(),
      aePhone: formatPhoneNumber(state.wholesaleAePhone || state.correspondentAePhone),
      aeEmail: state.wholesaleAeEmail.trim() || state.correspondentAeEmail.trim(),
      wholesaleWebsite: '',
      correspondentWebsite: '',
      website,
      wholesaleUsername: '',
      wholesalePassword: '',
      correspondentUsername: '',
      correspondentPassword: '',
      adminUsername: '',
      password: '',
      wholesaleCanIntegrateOptimalBlue: false,
      wholesaleHasIntegratedOptimalBlue: false,
      correspondentCanIntegrateOptimalBlue: false,
      correspondentHasIntegratedOptimalBlue: false,
      wholesaleEpoRule: state.canDoWholesale ? state.wholesaleEpoRule : getDefaultEpoRule('wholesale'),
      wholesaleEpdRule: state.canDoWholesale ? state.wholesaleEpdRule : getDefaultEpdRule('wholesale'),
      correspondentEpoRule: state.canGoCorrespondent ? state.correspondentEpoRule : getDefaultEpoRule('correspondent'),
      correspondentEpdRule: state.canGoCorrespondent ? state.correspondentEpdRule : getDefaultEpdRule('correspondent'),
      repurchaseRule: '',
      notes: '',
      wholesaleNotes: '',
      correspondentNotes: '',
      history: [],
      documents: [],
      createdAt: now,
      updatedAt: now
    };

    setSubmitting(true);
    try {
      await onCreate(investor);
      toast.success(`Created investor "${investor.investor}"`);
      resetAndClose();
    } catch {
      // useInvestors.addInvestor already toasts the specific failure message;
      // just keep the sheet open for a retry.
      setSubmitting(false);
    }
  };

  const renderError = (key: string) => touched && errors[key] ? (
    <p className="lw-aif-error">{errors[key]}</p>
  ) : null;

  // Structured EPO/EPD rule field (type + positive value) — produces a normalizer-valid
  // rule via create*Rule, matching the slide-over editor.
  const renderRuleField = (
    label: string,
    channel: 'wholesale' | 'correspondent',
    kind: 'epo' | 'epd',
    ruleKey: 'wholesaleEpoRule' | 'wholesaleEpdRule' | 'correspondentEpoRule' | 'correspondentEpdRule'
  ) => {
    const rule = state[ruleKey];
    const types = kind === 'epo' ? getValidEpoTypes(channel) : getValidEpdTypes(channel);
    const currentType = (rule?.type ?? 'payments') as EpoRuleType;
    const isCalendarMonthRule = kind === 'epo' && currentType === 'calendar_month_end_after_purchase';
    const buildRule = (type: EpoRuleType, value: number | undefined) => {
      if (kind === 'epo') {
        return createEpoRule(channel, type, value);
      }
      if (type === 'calendar_month_end_after_purchase') {
        throw new Error('Calendar-month rules are only supported for EPO');
      }
      return createEpdRule(channel, type, value);
    };
    return (
      <div className="lw-field">
        <span className="lw-label">{label}</span>
        <div style={{ display: 'flex', gap: 8, flexDirection: isCalendarMonthRule ? 'column' : 'row' }}>
          <select
            className="lw-input"
            style={{ minWidth: 0, maxWidth: '100%' }}
            aria-label={`${label} type`}
            value={currentType}
            onChange={(event) => setField(ruleKey, buildRule(event.target.value as EpoRuleType, normalizeRuleValue(rule?.value)))}
          >
            {types.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          {isCalendarMonthRule && <label className="lw-label" htmlFor={`${ruleKey}-months`}>Calendar months after purchase</label>}
          <input
            id={isCalendarMonthRule ? `${ruleKey}-months` : undefined}
            className="lw-input"
            type="number"
            min="1"
            step="1"
            max={currentType === 'payments' ? 12 : undefined}
            aria-label={isCalendarMonthRule ? `${label} calendar months after purchase` : `${label} value`}
            placeholder="Value"
            value={rule?.value || ''}
            onChange={(event) => setField(ruleKey, buildRule(currentType, normalizeRuleValue(event.target.value)))}
          />
        </div>
        {isCalendarMonthRule && <span className="text-xs text-muted-foreground">{rule.description} The purchase month is not counted.</span>}
        {renderError(ruleKey)}
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={(nextOpen) => {
      if (!nextOpen) {
        resetAndClose();
      }
    }}>
      <SheetContent side="right" hideClose className={`${LW_THEME_CLASS} lw-add-wizard p-0`}>
        <SheetTitle className="sr-only">Add investor</SheetTitle>
        <div className="lw-slideover-header">
          <div className="lw-aif-title-wrap">
            <button className="lw-icon-btn" aria-label="Close add investor" onClick={resetAndClose}>
              <X size={16} />
            </button>
            <span className="lw-avatar xl">{state.investor.trim().slice(0, 2).toUpperCase() || '?'}</span>
            <div>
              <div className="lw-profile-name-text">{state.investor.trim() || 'New investor'}</div>
              <div className="lw-profile-meta">Step {step + 1} of {STEPS.length} · {STEPS[step].label}</div>
            </div>
          </div>
        </div>

        <div className="lw-aif-stepper">
          {STEPS.map((item, index) => (
            <React.Fragment key={item.key}>
              <button
                type="button"
                className={`lw-aif-step ${index === step ? 'active' : ''} ${index < step ? 'done' : ''}`}
                onClick={() => {
                  if (index <= step || !stepErrorKeys.slice(0, index).some((keys) => keys.some((key) => errors[key]))) {
                    setTouched(false);
                    setStep(index);
                  } else {
                    setTouched(true);
                  }
                }}
              >
                <span className="lw-aif-step-dot">{index < step ? <Check size={12} /> : index + 1}</span>
                <span className="lw-aif-step-label">{item.label}</span>
              </button>
              {index < STEPS.length - 1 && <span className={`lw-aif-step-bar ${index < step ? 'done' : ''}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="lw-slideover-body lw-aif-body">
          <div className="lw-aif-content">
            {step === 0 && (
              <div className="lw-section-card lw-aif-card">
                <h4 className="lw-section-title"><Building2 size={14} /> Investor Identity</h4>
                <div className="lw-aif-section-body">
                  <label className="lw-field">
                    <span className="lw-label">Investor name *</span>
                    <input className="lw-input" value={state.investor} onChange={(event) => setField('investor', event.target.value)} placeholder="e.g. United Wholesale Mortgage" />
                    {renderError('investor')}
                  </label>
                  <label className="lw-field">
                    <span className="lw-label">Website</span>
                    <input className="lw-input" value={state.website} onChange={(event) => setField('website', event.target.value)} onBlur={() => setField('website', formatUrl(state.website))} placeholder="https://example.com" />
                    {renderError('website')}
                  </label>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4">
                <div className="lw-section-card lw-aif-card">
                  <h4 className="lw-section-title"><Check size={14} /> Channels</h4>
                  <div className="lw-aif-section-body">
                    <div className="lw-aif-hint">Enable at least one channel. Each enabled channel needs a status.</div>
                    <div className="lw-aif-channel-grid">
                      <button className={`lw-aif-channel ${state.canDoWholesale ? 'active' : ''}`} onClick={() => setField('canDoWholesale', !state.canDoWholesale)} type="button" aria-pressed={state.canDoWholesale}>
                        <span className="lw-channel-card-mark">W</span>
                        <span className="lw-aif-channel-name">Wholesale</span>
                        <span className="lw-aif-channel-check">{state.canDoWholesale && <Check size={12} />}</span>
                      </button>
                      <button className={`lw-aif-channel ${state.canGoCorrespondent ? 'active' : ''}`} onClick={() => setField('canGoCorrespondent', !state.canGoCorrespondent)} type="button" aria-pressed={state.canGoCorrespondent}>
                        <span className="lw-channel-card-mark c">C</span>
                        <span className="lw-aif-channel-name">Correspondent</span>
                        <span className="lw-aif-channel-check">{state.canGoCorrespondent && <Check size={12} />}</span>
                      </button>
                    </div>
                    {renderError('channels')}
                  </div>
                </div>

                {state.canDoWholesale && (
                  <StatusSection
                    label="Wholesale status"
                    channel="wholesale"
                    status={state.status}
                    date={state.approvedAsOfDate}
                    error={renderError('approvedAsOfDate')}
                    onStatus={(value) => setField('status', value)}
                    onDate={(value) => setField('approvedAsOfDate', value)}
                  />
                )}

                {state.canGoCorrespondent && (
                  <StatusSection
                    label="Correspondent status"
                    channel="correspondent"
                    status={state.correspondentStatus}
                    date={state.correspondentApprovedAsOfDate}
                    error={renderError('correspondentApprovedAsOfDate')}
                    onStatus={(value) => setField('correspondentStatus', value)}
                    onDate={(value) => setField('correspondentApprovedAsOfDate', value)}
                  />
                )}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                {state.canDoWholesale && (
                  <ProductSection channel="wholesale" title="Wholesale products" selected={state.wholesaleProducts} onToggle={(product) => setField('wholesaleProducts', toggleValue(state.wholesaleProducts, product))} />
                )}
                {state.canGoCorrespondent && (
                  <ProductSection channel="correspondent" title="Correspondent products" selected={state.correspondentProducts} onToggle={(product) => setField('correspondentProducts', toggleValue(state.correspondentProducts, product))} />
                )}
                {((state.canDoWholesale && state.wholesaleProducts.length === 0) || (state.canGoCorrespondent && state.correspondentProducts.length === 0)) && (
                  <div className="lw-aif-nudge">Products are recommended, not required. You can finish creating the investor now and add products later from the profile.</div>
                )}
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                {state.canDoWholesale && (
                  <ContactSection
                    channel="wholesale"
                    title="Wholesale Account Executive"
                    name={state.wholesaleAeName}
                    email={state.wholesaleAeEmail}
                    phone={state.wholesaleAePhone}
                    emailError={renderError('wholesaleAeEmail')}
                    phoneError={renderError('wholesaleAePhone')}
                    onName={(value) => setField('wholesaleAeName', value)}
                    onEmail={(value) => setField('wholesaleAeEmail', value)}
                    onPhone={(value) => setField('wholesaleAePhone', formatPhoneNumber(value))}
                  />
                )}
                {state.canGoCorrespondent && (
                  <ContactSection
                    channel="correspondent"
                    title="Correspondent Account Executive"
                    name={state.correspondentAeName}
                    email={state.correspondentAeEmail}
                    phone={state.correspondentAePhone}
                    emailError={renderError('correspondentAeEmail')}
                    phoneError={renderError('correspondentAePhone')}
                    onName={(value) => setField('correspondentAeName', value)}
                    onEmail={(value) => setField('correspondentAeEmail', value)}
                    onPhone={(value) => setField('correspondentAePhone', formatPhoneNumber(value))}
                  />
                )}
                <div className="lw-aif-nudge">Contacts are recommended but not required. Add or edit them anytime from the investor profile.</div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-4">
                <div className="lw-section-card lw-aif-card">
                  <h4 className="lw-section-title"><Settings2 size={14} /> Terms</h4>
                  <div className="lw-aif-section-body">
                    {/* Delegation is set per-product in the investor slide-over (item 6), not here. */}
                    {state.canDoWholesale && (
                      <div className="lw-edit-grid">
                        <label className="lw-field">
                          <span className="lw-label">LPC %</span>
                          <input className="lw-input" inputMode="decimal" value={state.wholesaleLenderPaidComp} onChange={(event) => setField('wholesaleLenderPaidComp', event.target.value)} placeholder="1.75" />
                          {renderError('wholesaleLenderPaidComp')}
                        </label>
                        <label className="lw-field">
                          <span className="lw-label">Additional Lender Fee</span>
                          <input className="lw-input" inputMode="decimal" value={state.wholesaleAdditionalLendwiseFee} onChange={(event) => setField('wholesaleAdditionalLendwiseFee', event.target.value)} placeholder="0" />
                          {renderError('wholesaleAdditionalLendwiseFee')}
                        </label>
                        {renderRuleField('Wholesale EPO', 'wholesale', 'epo', 'wholesaleEpoRule')}
                        {renderRuleField('Wholesale EPD', 'wholesale', 'epd', 'wholesaleEpdRule')}
                      </div>
                    )}
                    {state.canGoCorrespondent && (
                      <div className="lw-edit-grid">
                        {renderRuleField('Correspondent EPO', 'correspondent', 'epo', 'correspondentEpoRule')}
                        {renderRuleField('Correspondent EPD', 'correspondent', 'epd', 'correspondentEpdRule')}
                        <label className="lw-field">
                          <span className="lw-label">Delegated net worth</span>
                          <input className="lw-input" inputMode="decimal" value={state.netWorthDelegated} onChange={(event) => setField('netWorthDelegated', event.target.value)} placeholder="1,000,000" />
                          {renderError('netWorthDelegated')}
                        </label>
                        <label className="lw-field">
                          <span className="lw-label">Non-delegated net worth</span>
                          <input className="lw-input" inputMode="decimal" value={state.netWorthNonDelegated} onChange={(event) => setField('netWorthNonDelegated', event.target.value)} placeholder="1,000,000" />
                          {renderError('netWorthNonDelegated')}
                        </label>
                      </div>
                    )}
                  </div>
                </div>
                <div className="lw-aif-nudge">All terms are optional at creation. The new investor will be saved and you can refine terms from its profile.</div>
              </div>
            )}
          </div>
        </div>

        <div className="lw-aif-footer">
          <span className="lw-meta-text">* required</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="lw-btn lw-btn-secondary" onClick={resetAndClose} type="button">Cancel</button>
            {step > 0 && <button className="lw-btn lw-btn-secondary" onClick={goBack} type="button"><ArrowLeft size={14} /> Back</button>}
            {step < STEPS.length - 1
              ? <button className="lw-btn lw-btn-primary" onClick={goNext} type="button">Next</button>
              : <button className="lw-btn lw-btn-primary" onClick={() => void submit()} disabled={submitting} type="button">{submitting ? 'Creating…' : 'Create investor'}</button>}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

interface StatusSectionProps {
  label: string;
  channel: ChannelView;
  status: Status;
  date: string;
  error: React.ReactNode;
  onStatus: (status: Status) => void;
  onDate: (date: string) => void;
}

const channelAccent = (channel: ChannelView) => (
  <span className={`lw-aif-accent ${channel === 'correspondent' ? 'c' : ''}`}>
    {channel === 'correspondent' ? 'C' : 'W'}
  </span>
);

const StatusSection: React.FC<StatusSectionProps> = ({ label, channel, status, date, error, onStatus, onDate }) => (
  <div className="lw-section-card lw-aif-card">
    <h4 className="lw-section-title">{channelAccent(channel)} {label}</h4>
    <div className="lw-aif-section-body">
      <div className="lw-aif-status-row">
        {statusOptions.map((option) => (
          <button key={option} className={`lw-status-choice ${status === option ? `active ${option.toLowerCase()}` : ''}`} type="button" onClick={() => onStatus(option)}>
            {option}
          </button>
        ))}
        <label className="lw-field lw-aif-date">
          <span className="lw-label">Effective date {status === 'Approved' ? '*' : ''}</span>
          <input className="lw-input" type="date" value={date} onChange={(event) => onDate(event.target.value)} />
          {error}
        </label>
      </div>
    </div>
  </div>
);

const ProductSection: React.FC<{ channel: ChannelView; title: string; selected: string[]; onToggle: (product: string) => void }> = ({ channel, title, selected, onToggle }) => (
  <div className="lw-section-card lw-aif-card">
    <h4 className="lw-section-title">{channelAccent(channel)} {title}</h4>
    <div className="lw-aif-section-body">
      <div className="lw-aif-products">
        {PRODUCT_TYPES.map((product) => (
          <button key={product} type="button" className={`lw-dist-pill ${selected.includes(product) ? 'active' : ''}`} onClick={() => onToggle(product)} aria-pressed={selected.includes(product)}>
            {selected.includes(product) && <Check size={12} />}
            {product}
          </button>
        ))}
      </div>
    </div>
  </div>
);

interface ContactSectionProps {
  channel: ChannelView;
  title: string;
  name: string;
  email: string;
  phone: string;
  emailError: React.ReactNode;
  phoneError: React.ReactNode;
  onName: (value: string) => void;
  onEmail: (value: string) => void;
  onPhone: (value: string) => void;
}

const ContactSection: React.FC<ContactSectionProps> = ({
  channel,
  title,
  name,
  email,
  phone,
  emailError,
  phoneError,
  onName,
  onEmail,
  onPhone
}) => (
  <div className="lw-section-card lw-aif-card">
    <h4 className="lw-section-title">{channelAccent(channel)} <User size={14} /> {title}</h4>
    <div className="lw-aif-section-body">
      <label className="lw-field"><span className="lw-label">Name</span><input className="lw-input" value={name} onChange={(event) => onName(event.target.value)} placeholder="Account executive name" /></label>
      <div className="lw-edit-grid">
        <label className="lw-field"><span className="lw-label"><Mail size={12} /> Email</span><input className="lw-input" value={email} onChange={(event) => onEmail(event.target.value)} placeholder="name@investor.com" />{emailError}</label>
        <label className="lw-field"><span className="lw-label"><Phone size={12} /> Phone</span><input className="lw-input" value={phone} onChange={(event) => onPhone(event.target.value)} placeholder="555-555-0100" />{phoneError}</label>
      </div>
    </div>
  </div>
);

export default AddInvestorWizard;
